import { TonLegSubStatus } from './flow'
import { findStep, type Step } from './progress'

interface TonProgressState {
  status: string
  lastError: string | null
  quote: unknown
  tonLegSubStatus: (typeof TonLegSubStatus)[keyof typeof TonLegSubStatus] | null
  steps: readonly Step[]
}

/**
 * Helpers around CrosschainBuyState.tonLegSubStatus.
 *
 * `hasUsdtToTonCompleted`: usdtToTon has settled. True for both
 * `TON_TO_SPOTTY` (tonToSpotty actively running, two-phase or chained path) AND
 * `TON_TO_SPOTTY_PENDING` (usdtToTon done, awaiting user-gesture click for
 * tonToSpotty via the Continue button — two-phase mobile path).
 *
 * Used for execute-swap retry routing AND for RouteStepper Step 2
 * (USDT→TON) checkmark. Step 3 (TON→SPOTTY) "active" rendering should
 * use the narrower `isTonToSpottyInFlight` predicate below to avoid showing a
 * spinner during the awaiting-tap gap.
 */
export const hasUsdtToTonCompleted = (status: TonProgressState['tonLegSubStatus']): boolean =>
  status === TonLegSubStatus.TON_TO_SPOTTY || status === TonLegSubStatus.TON_TO_SPOTTY_PENDING

/** Canonical progress wins over the transient in-memory TON sub-status. */
export const hasUsdtToTonCompletedForState = (state: TonProgressState): boolean =>
  findStep(state, 'usdt_to_ton')?.status === 'completed' ||
  hasUsdtToTonCompleted(state.tonLegSubStatus)

/**
 * True while the completed USDT→TON hop is waiting for the user's fresh
 * gesture to submit TON→SPOTTY. The canonical step ladder makes this survive
 * hydration even when the parent lifecycle is projected as
 * `destination_swap_ready` and the local sub-status was lost.
 */
export const isTonToSpottyContinuationReady = (state: TonProgressState): boolean => {
  if (state.tonLegSubStatus === TonLegSubStatus.TON_TO_SPOTTY_PENDING) return true
  // A non-null local phase is an active, more recent transition. In
  // particular, TON_TO_SPOTTY is set synchronously as the double-tap lock;
  // stale canonical completed/pending steps must not reopen the action.
  if (state.tonLegSubStatus !== null) return false
  return (
    findStep(state, 'usdt_to_ton')?.status === 'completed' &&
    findStep(state, 'ton_to_spotty')?.status === 'pending'
  )
}

/**
 * tonToSpotty is being executed right now. Distinct from `hasUsdtToTonCompleted` so
 * the UI can differentiate "usdtToTon done, waiting for user click" (Step 3
 * green ✓, Step 4 grey pending) from "tonToSpotty firing in Tonkeeper" (Step 3
 * green ✓, Step 4 spinner active).
 */
export const isTonToSpottyInFlight = (status: TonProgressState['tonLegSubStatus']): boolean =>
  status === TonLegSubStatus.TON_TO_SPOTTY

export const isRetryableUsdtToGramFailure = (state: TonProgressState): boolean => {
  const bridgeStep = findStep(state, 'bridge')
  const usdtToTonStep = findStep(state, 'usdt_to_ton')

  return (
    state.status === 'error' &&
    state.lastError === 'ton_tx_failed' &&
    !!state.quote &&
    bridgeStep?.status === 'completed' &&
    usdtToTonStep?.status === 'failed' &&
    !usdtToTonStep.txHash
  )
}
